// v283-ratgeber-blocks
import "@/App.css";
import "./styles/cro.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { I18nProvider } from "./context/I18nContext";
import ErrorBoundary from "./components/ErrorBoundary";
import CartSaveModal from "./components/shop/CartSaveModal";
import ExitIntentPopup from "./components/shop/ExitIntentPopup";
import { installErrorReporter } from "./utils/errorReporter";
import { installAnalytics } from "./utils/analytics";
import { captureAttribution } from "./utils/attribution";

// Install global JS error / fetch error reporter once at startup
installErrorReporter();
installAnalytics();
captureAttribution();

// Critical path - load immediately
import ShopLanding from "./pages/ShopLanding";
import GewaehrleistungPage from "./pages/GewaehrleistungPage";
import ProductListPage from "./pages/ProductListPage";
import ProductDetailPage from "./pages/ProductDetailPage";

// Lazy-loaded pages
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const WiderrufButtonPage = lazy(() => import("./pages/WiderrufButtonPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const FahrwerkListPage = lazy(() => import("./pages/FahrwerkListPage"));
const GewinnspielPage = lazy(() => import("./pages/GewinnspielPage"));
const TeilnahmebedingungenGewinnspielPage = lazy(() => import("./pages/TeilnahmebedingungenGewinnspielPage"));
const RaffleAdminPage = lazy(() => import("./pages/admin/RaffleAdminPage"));
const GaranLabelsPage = lazy(() => import("./pages/admin/GaranLabelsPage"));
const LosePage = lazy(() => import("./pages/LosePage"));
const TeilnahmebedingungenLosePage = lazy(() => import("./pages/TeilnahmebedingungenLosePage"));
const TicketAdminPage = lazy(() => import("./pages/admin/TicketAdminPage"));
import { RaffleFloater } from "./components/raffle/RaffleBanner";
import WhatsAppButton from "./components/shop/WhatsAppButton";

// Globaler WhatsApp-Support-Button auf allen Seiten (außer Admin)
function GlobalWhatsApp() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <WhatsAppButton />;
}
import { RaffleStickyBar } from "./components/raffle/TicketBanner";
const FahrwerkDetailPage = lazy(() => import("./pages/FahrwerkDetailPage"));
const FahrwerkSeoPage = lazy(() => import("./pages/FahrwerkSeoPage"));
const ManufacturerPage = lazy(() => import("./pages/ManufacturerPage"));
const ManufacturerIndexPage = lazy(() => import("./pages/ManufacturerIndexPage"));
const TuningPage = lazy(() => import("./pages/TuningPage"));
const TuningIndexPage = lazy(() => import("./pages/TuningIndexPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const ComparisonIndexPage = lazy(() => import("./pages/ComparisonIndexPage"));
const RatgeberIndexPage = lazy(() => import("./pages/RatgeberIndexPage"));
const RatgeberPage = lazy(() => import("./pages/RatgeberPage"));

// Admin (always lazy)
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const OrdersPage = lazy(() => import("./pages/admin/OrdersPage"));
const ProductsPage = lazy(() => import("./pages/admin/ProductsPage"));
const CustomersPage = lazy(() => import("./pages/admin/CustomersPage"));
const StaffPage = lazy(() => import("./pages/admin/StaffPage"));  // v305b
const SeoPagesPage = lazy(() => import("./pages/admin/SeoPagesPage"));
const CouponsPage = lazy(() => import("./pages/admin/CouponsPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const StatisticsPage = lazy(() => import("./pages/admin/StatisticsPage"));
const CategoriesPage = lazy(() => import("./pages/admin/CategoriesPage"));
const CategoryProductsPage = lazy(() => import("./pages/admin/CategoryProductsPage"));
const PagesPage = lazy(() => import("./pages/admin/PagesPage"));
const EmailTemplatesPage = lazy(() => import("./pages/admin/EmailTemplatesPage"));
const LandingEditorPage = lazy(() => import("./pages/admin/LandingEditorPage"));
const EmailMarketingPage = lazy(() => import("./pages/admin/EmailMarketingPage"));
const ReviewsAdminPage = lazy(() => import("./pages/admin/ReviewsAdminPage"));
const FitmentAdminPage = lazy(() => import("./pages/admin/FitmentAdminPage"));
const FahrwerkAdminPage = lazy(() => import("./pages/admin/FahrwerkAdminPage"));
const FahrwerkCategoriesPage = lazy(() => import("./pages/admin/FahrwerkCategoriesPage"));
const ScraperPage = lazy(() => import("./pages/admin/ScraperPage"));
const ChiptuningScraperPage = lazy(() => import("./pages/admin/ChiptuningScraperPage"));
const StreetecScraperPage = lazy(() => import("./pages/admin/StreetecScraperPage"));
const ProductCreatePage = lazy(() => import("./pages/admin/ProductCreatePage"));
const LexwarePage = lazy(() => import("./pages/admin/LexwarePage"));
const PacklinkPage = lazy(() => import("./pages/admin/PacklinkPage"));
const ImportPage = lazy(() => import("./pages/admin/ImportPage"));
const IntegrationsPage = lazy(() => import("./pages/admin/IntegrationsPage"));
const VouchersPage = lazy(() => import("./pages/admin/VouchersPage"));
const QuotesPage = lazy(() => import("./pages/admin/QuotesPage"));
const ManufacturersPage = lazy(() => import("./pages/admin/ManufacturersPage"));
const PromoBannersPage = lazy(() => import("./pages/admin/PromoBannersPage"));
const CapristoPage = lazy(() => import("./pages/admin/CapristoPage"));
const BastuckPage = lazy(() => import("./pages/admin/BastuckPage"));
const RemusPage = lazy(() => import("./pages/admin/RemusPage"));
const RemusComponentsPage = lazy(() => import("./pages/admin/RemusComponentsPage"));
const AprPage = lazy(() => import("./pages/admin/AprPage"));
const DinanPage = lazy(() => import("./pages/admin/DinanPage"));
const StollPage = lazy(() => import("./pages/admin/StollPage"));
const Parts55Page = lazy(() => import("./pages/admin/Parts55Page"));
const AulitzkyPage = lazy(() => import("./pages/admin/AulitzkyPage"));
const DiederichsPage = lazy(() => import("./pages/admin/DiederichsPage"));
const NovitecPage = lazy(() => import("./pages/admin/NovitecPage"));
const WagnerPage = lazy(() => import("./pages/admin/WagnerPage"));
const ZaeroPage = lazy(() => import("./pages/admin/ZaeroPage"));
const MhPipesPage = lazy(() => import("./pages/admin/MhPipesPage"));
const MaxtonImportPage = lazy(() => import("./pages/admin/MaxtonImportPage"));
const AsrPage = lazy(() => import("./pages/admin/AsrPage"));
const BartekPage = lazy(() => import("./pages/admin/BartekPage"));
const CsrPage = lazy(() => import("./pages/admin/CsrPage"));  // v312b
const GrailSyncPage = lazy(() => import("./pages/admin/GrailSyncPage"));
const SyncMonitorPage = lazy(() => import("./pages/admin/SyncMonitorPage"));
const ScraperCleanupPage = lazy(() => import("./pages/admin/ScraperCleanupPage"));
const RatgeberAdminPage = lazy(() => import("./pages/admin/RatgeberAdminPage"));
const SitemapManagerPage = lazy(() => import("./pages/admin/SitemapManagerPage"));  // v284
const RatgeberEditorPage = lazy(() => import("./pages/admin/RatgeberEditorPage"));  // v283
const LegalDocsPage = lazy(() => import("./pages/admin/LegalDocsPage"));
const ErrorsPage = lazy(() => import("./pages/admin/ErrorsPage"));
const CsrBestandPage = lazy(() => import("./pages/admin/CsrBestandPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const AdvancedAnalyticsPage = lazy(() => import("./pages/admin/AdvancedAnalyticsPage"));
const AdAnalyticsPage = lazy(() => import("./pages/admin/AdAnalyticsPage"));
const GoogleAdsAiPage = lazy(() => import("./pages/admin/GoogleAdsAiPage"));
const NewsletterUnsubscribePage = lazy(() => import("./pages/NewsletterUnsubscribePage"));
const EmailUnsubscribePage = lazy(() => import("./pages/EmailUnsubscribePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

// TyreSystem (Reifen, Felgen, Räder, Zubehör) - hidden behind feature flag
const ReifenPage = lazy(() => import("./pages/ReifenPage"));
const FelgenPage = lazy(() => import("./pages/FelgenPage"));
const KompletteraederPage = lazy(() => import("./pages/KompletteraederPage"));
const ZubehoerPage = lazy(() => import("./pages/ZubehoerPage"));
const TyreSystemDetailPage = lazy(() => import("./pages/TyreSystemDetailPage"));
const ReifenKonfiguratorPage = lazy(() => import("./pages/ReifenKonfiguratorPage"));
const TyreSystemPage = lazy(() => import("./pages/admin/TyreSystemPage"));

// Lazy-loaded named exports need wrappers
const OrderListPage = lazy(() => import("./pages/admin/OrdersPage").then(m => ({ default: m.OrderListPage })));
const OrderDetailPage = lazy(() => import("./pages/admin/OrdersPage").then(m => ({ default: m.OrderDetailPage })));
const AdminProductList = lazy(() => import("./pages/admin/ProductsPage").then(m => ({ default: m.ProductListPage })));
const AdminProductDetail = lazy(() => import("./pages/admin/ProductsPage").then(m => ({ default: m.ProductDetailPage })));
const CustomerListPage = lazy(() => import("./pages/admin/CustomersPage").then(m => ({ default: m.CustomerListPage })));
const CustomerDetailPage = lazy(() => import("./pages/admin/CustomersPage").then(m => ({ default: m.CustomerDetailPage })));
const PagesListPage = lazy(() => import("./pages/admin/PagesPage").then(m => ({ default: m.PagesListPage })));
const StockPage = lazy(() => import("./pages/admin/StockPage"));  // v300a
const PageEditPage = lazy(() => import("./pages/admin/PagesPage").then(m => ({ default: m.PageEditPage })));

const LoadingFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', background: 'var(--bg-default, #0a0a0a)', color: 'var(--fg-muted, #888)' }}>
    <div data-testid="loading-fallback" style={{ textAlign: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid transparent', borderTop: '3px solid var(--primary, #e63946)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
      Laden...
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <ThemeProvider>
      <I18nProvider>
      <CartProvider>
        <Router>
          <div className="App">
            <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<ShopLanding />} />
              <Route path="/shop" element={<ProductListPage />} />
              <Route path="/fahrwerke" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><FahrwerkListPage /></Suspense>} />
              <Route path="/fahrwerke/marke/*" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><FahrwerkSeoPage /></Suspense>} />
              <Route path="/fahrwerke/typ/*" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><FahrwerkSeoPage /></Suspense>} />
              <Route path="/fahrwerke/fahrzeug/*" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><FahrwerkSeoPage /></Suspense>} />
              <Route path="/fahrwerke/:slug" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><FahrwerkDetailPage /></Suspense>} />
              <Route path="/p/:slug" element={<ProductDetailPage />} />
              <Route path="/marken" element={<ManufacturerIndexPage />} />
              <Route path="/marke/:slug" element={<ManufacturerPage />} />
              <Route path="/tuning" element={<TuningIndexPage />} />
              <Route path="/tuning/:vehicle" element={<TuningPage />} />
              <Route path="/tuning/:vehicle/marke/:brand" element={<TuningPage />} />
              <Route path="/tuning/:vehicle/:category" element={<TuningPage />} />
              <Route path="/vergleich" element={<ComparisonIndexPage />} />
              <Route path="/vergleich/:slug" element={<ComparisonPage />} />
              <Route path="/ratgeber" element={<RatgeberIndexPage />} />
              <Route path="/ratgeber/:slug" element={<RatgeberPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/payment-success" element={<CheckoutPage />} />
              <Route path="/checkout/payment-cancelled" element={<CheckoutPage />} />
              <Route path="/kontakt" element={<ContactPage />} />
              <Route path="/newsletter/abmelden" element={<NewsletterUnsubscribePage />} />
              <Route path="/email/abmelden" element={<EmailUnsubscribePage />} />
              <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
              <Route path="/passwort-reset" element={<ResetPasswordPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/mein-konto" element={<AccountPage />} />
              <Route path="/impressum" element={<LegalPage pageType="impressum" />} />
              <Route path="/agb" element={<LegalPage pageType="agb" />} />
              <Route path="/datenschutz" element={<LegalPage pageType="datenschutz" />} />
              <Route path="/versand" element={<LegalPage pageType="versand" />} />
              <Route path="/widerruf" element={<LegalPage pageType="widerruf" />} />
              <Route path="/vertrag-widerrufen" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><WiderrufButtonPage /></Suspense>} />
              <Route path="/cookie-richtlinie" element={<LegalPage pageType="cookie-richtlinie" />} />
              <Route path="/gewinnspiel" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><GewinnspielPage /></Suspense>} />
              <Route path="/teilnahmebedingungen-gewinnspiel" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><TeilnahmebedingungenGewinnspielPage /></Suspense>} />
              <Route path="/lose" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><LosePage /></Suspense>} />
              <Route path="/teilnahmebedingungen-lose" element={<Suspense fallback={<div style={{minHeight:'80vh'}}/>}><TeilnahmebedingungenLosePage /></Suspense>} />

              {/* TyreSystem Shop (hidden behind feature flag) */}
              <Route path="/reifen" element={<ReifenPage />} />
              <Route path="/felgen" element={<FelgenPage />} />
              <Route path="/raeder" element={<KompletteraederPage />} />
              <Route path="/zubehoer" element={<ZubehoerPage />} />
              <Route path="/reifen/:slug" element={<TyreSystemDetailPage />} />
              <Route path="/felgen/:slug" element={<TyreSystemDetailPage />} />
              <Route path="/raeder/:slug" element={<TyreSystemDetailPage />} />
              <Route path="/zubehoer/:slug" element={<TyreSystemDetailPage />} />
              {/* Legacy fallback */}
              <Route path="/tyresystem/:slug" element={<TyreSystemDetailPage />} />
              <Route path="/reifen-konfigurator" element={<ReifenKonfiguratorPage />} />


              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="orders" element={<OrderListPage />} />
                <Route path="garan-labels" element={<GaranLabelsPage />} />
                <Route path="orders/:orderNumber" element={<OrderDetailPage />} />
                <Route path="products" element={<AdminProductList />} />
                <Route path="products/new" element={<ProductCreatePage />} />
                <Route path="products/:alias" element={<AdminProductDetail />} />
                <Route path="stock" element={<StockPage />} />  {/* v300a */}
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="category-products" element={<CategoryProductsPage />} />
                <Route path="customers" element={<CustomerListPage />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="customers/:email" element={<CustomerDetailPage />} />
                <Route path="coupons" element={<CouponsPage />} />
                <Route path="statistics" element={<StatisticsPage />} />
                <Route path="pages" element={<PagesListPage />} />
                <Route path="seo-pages" element={<SeoPagesPage />} />
                <Route path="pages/:slug" element={<PageEditPage />} />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="email-marketing" element={<EmailMarketingPage />} />
                <Route path="reviews" element={<ReviewsAdminPage />} />
                <Route path="fitment" element={<FitmentAdminPage />} />
                <Route path="fahrwerk" element={<FahrwerkAdminPage />} />
                <Route path="fahrwerk-kategorien" element={<FahrwerkCategoriesPage />} />
                <Route path="lexware" element={<LexwarePage />} />
                <Route path="packlink" element={<PacklinkPage />} />
                <Route path="import" element={<ImportPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="vouchers" element={<VouchersPage />} />
                <Route path="quotes" element={<QuotesPage />} />
                <Route path="manufacturers" element={<ManufacturersPage />} />
                <Route path="promo-banners" element={<PromoBannersPage />} />
                <Route path="scraper" element={<ScraperPage />} />
                <Route path="chiptuning-db" element={<ChiptuningScraperPage />} />
                <Route path="streetec-scraper" element={<StreetecScraperPage />} />
                <Route path="capristo" element={<CapristoPage />} />
                <Route path="bastuck" element={<BastuckPage />} />
                <Route path="remus" element={<RemusPage />} />
                <Route path="apr" element={<AprPage />} />
                <Route path="dinan" element={<DinanPage />} />
                <Route path="stoll" element={<StollPage />} />
                <Route path="55parts" element={<Parts55Page />} />
                <Route path="aulitzky" element={<AulitzkyPage />} />
                <Route path="diederichs" element={<DiederichsPage />} />
                <Route path="novitec" element={<NovitecPage />} />
                <Route path="wagner" element={<WagnerPage />} />
                <Route path="zaero" element={<ZaeroPage />} />
                <Route path="mhpipes" element={<MhPipesPage />} />
                <Route path="maxton-import" element={<MaxtonImportPage />} />
                <Route path="bartek" element={<BartekPage />} />
                <Route path="asr" element={<AsrPage />} />
                <Route path="grail" element={<GrailSyncPage />} />
                <Route path="csr" element={<CsrPage />} />  {/* v312b */}
                <Route path="remus-components" element={<RemusComponentsPage />} />
                <Route path="sync-monitor" element={<SyncMonitorPage />} />
                <Route path="scraper-cleanup" element={<ScraperCleanupPage />} />
                <Route path="ratgeber" element={<RatgeberAdminPage />} />
                <Route path="sitemap" element={<SitemapManagerPage />} />  {/* v284 */}
                <Route path="ratgeber/edit/:slug" element={<RatgeberEditorPage />} />
                <Route path="tyresystem" element={<TyreSystemPage />} />
                <Route path="legal-docs" element={<LegalDocsPage />} />
                <Route path="errors" element={<ErrorsPage />} />
                <Route path="csr-bestand" element={<CsrBestandPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="analytics-advanced" element={<AdvancedAnalyticsPage />} />
                <Route path="ad-analytics" element={<AdAnalyticsPage />} />
                <Route path="google-ads" element={<GoogleAdsAiPage />} />
                <Route path="landing-editor" element={<LandingEditorPage />} />
                <Route path="raffle" element={<RaffleAdminPage />} />
                <Route path="tickets" element={<TicketAdminPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Routes>
            </Suspense>
            <RaffleFloater />
            <RaffleStickyBar />
            <CartSaveModal />
            <ExitIntentPopup />
            <GlobalWhatsApp />
          </div>
        </Router>
      </CartProvider>
      </I18nProvider>
      </ThemeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;

